import { List } from "@lightningjs/ui";
import { Lightning } from "@lightningjs/sdk";
import { MediaList } from "../MediaList/MediaList";
import { getInfoFromTitle, MENU_WIDTH } from "../../../lib/utils";
import { CategoryModel, MediaModel } from "../../../lib/models";
import theme from "../../../lib/theme";
import { MediaCategory } from "../MediaCategory/MediaCategory";
import { MovieDetails } from "../MovieDetails/MovieDetails";

interface CategoryListTemplateSpec extends Lightning.Component.TemplateSpec {
    requestMoreItems: (offset?: number) => Promise<CategoryModel[]>;
    Top: {
        Category: typeof MediaCategory;
        Details: typeof MovieDetails;
    };
    Container: {
        List: typeof List;
    };
}

export class CategoryList
    extends Lightning.Component<CategoryListTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<CategoryListTemplateSpec>
{
    _categories: CategoryModel[] = [];
    _boundEventHandler?: any;
    _requestMoreItems: any;

    static override _template(): Lightning.Component.Template<CategoryListTemplateSpec> {
        return {
            x: 0,
            // y: 50,
            y: 0,
            w: theme.layout.w - MENU_WIDTH,
            flex: { direction: "column" },
            collision: true,
            zIndex: 10,
            Top: {
                w: theme.banner.w - MENU_WIDTH,
                h: theme.banner.h,
                Category: {
                    flexItem: { marginBottom: 8 },
                    type: MediaCategory
                },
                Details: {
                    type: MovieDetails
                }
            },
            Container: {
                // w: theme.layout.bannerW,
                h: theme.layout.h - theme.banner.h,
                w: theme.layout.w - MENU_WIDTH,
                x: 0,
                y: 32,
                clipping: true,
                collision: true,
                List: {
                    type: List,
                    collision: true,
                    direction: "column",
                    spacing: 36,
                    h: (h) => h,
                    w: (w) => w,
                    y: 0,
                    x: 0,
                    scroll: {
                        after: 1
                    },
                    gcThreshold: 2,
                    requestThreshold: 4,
                    scrollTransition: {
                        duration: 0.4,
                        timingFunction: "linear"
                    },
                    enableRequests: true,
                    signals: { onIndexChanged: true, onRequestItems: true }
                }
            }
        };
    }
    readonly Container = this.getByRef("Container")!;
    readonly List = this.Container.getByRef("List")!;
    readonly Top = this.getByRef("Top")!;
    readonly Category = this.Top.getByRef("Category")!;
    readonly Details = this.Top.getByRef("Details")!;

    override _setup() {
        this._boundEventHandler = this._updateMovieInfo.bind(this);
    }

    override _getFocused(): Lightning.Component | null | undefined {
        return this.List;
    }

    override _active() {
        this.application.on("updateSelectedMovie", this._boundEventHandler);

        if (this.List.items.length === 0) {
            this.onRequestItems();
        }
    }

    override _inactive() {
        this.application.off("updateSelectedMovie", this._boundEventHandler);
    }

    _updateMovieInfo(data: MediaModel) {
        this.Category.patch({
            label: getInfoFromTitle(data?.title).category
        });
    }

    reload(categories: CategoryModel[]) {
        this._categories = categories;

        this.List.reload(
            this._categories
                .map((category) => ({
                    collision: true,
                    type: MediaList,
                    categoryId: category.id,
                    isContinueWatching: category.isContinueWatching,
                    isTrending: category.isTrending,
                    // categoryTranslations: category.translations,
                    items: category.items || [],
                    lastIndex: 0,
                    categoryKey: category.name,
                    // todo dynamic, 158 is category height
                    h: (category.id === 'apps' ? theme.externalAppItem.h : theme.mediaItem.h) + 158
                }))
                .filter((cat: CategoryModel) => !!cat.items.length)
        );

        for (const wrapper of this.List.children) {
            wrapper?.patch({
                collision: true
            });
        }
    }

    addItems(categories: CategoryModel[]) {
        this._categories = categories;

        if (this.List.items?.length) return;

        this.reload(categories);
    }

    reloadMovies(categories: CategoryModel[]) {
        this.List.items.map((item: any) => {
            const category = categories.find((cat) => cat.id === item.id)!;
            if (item.items.length !== category.items!.length) {
                item.items = category.items;
            }
        });
    }

    onIndexChanged(data: { previousIndex: number; index: number }) {
        if (this.List.items) {
            if (data.previousIndex < data.index) {
                if (typeof this.List?.items[data.previousIndex]?.fadeOut !== "undefined") {
                    this.List.items[data.previousIndex].fadeOut();
                }
            } else {
                if (typeof this.List?.items[data.index - 1]?.fadeOut !== "undefined") {
                    this.List.items[data.index - 1].fadeOut();
                }
            }

            if (this.List.items[data.index] && typeof this.List.items[data.index].fadeIn !== "undefined") {
                this.List.items[data.index].fadeIn();
            }
        }
    }

    async onRequestItems() {
        if (!this._requestMoreItems) return;

        const data = await this._requestMoreItems(this._categories.length - 1);

        this._categories = this._categories.concat(data);

        return data
            .map((category: CategoryModel) => ({
                collision: true,
                type: MediaList,
                categoryId: category.id,
                isContinueWatching: category.isContinueWatching,
                isTrending: category.isTrending,
                // categoryTranslations: category.translations,
                items: category.items || [],
                lastIndex: 0,
                categoryKey: category.name
            }))
            .filter((cat: CategoryModel) => !!cat.items.length);
    }

    $categorySelected(listIndex: number) {
        this.List.setIndex(listIndex);
    }

    $categoryMoviesLoaded({ id, movies }: { id: string; movies: MediaModel[] }) {
        this._categories.map((category) => {
            if (category.id === id) {
                category.items = movies;
            }

            return category;
        });
    }

    set requestMoreItems(value: any) {
        this._requestMoreItems = value;
    }

    get items() {
        return this.List.items;
    }

    get index() {
        return this.List.index;
    }

    set index(value: number) {
        this.List.setIndex(value);
    }
}
